import React, { useState, useEffect } from 'react';
import { Info, X } from 'lucide-react';

export default function BannerSizingInfo({ style }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', padding: 0,
          color: 'var(--navy, #1e3a8a)', fontSize: 12.5, cursor: 'pointer',
          textDecoration: 'underline', textUnderlineOffset: 2,
          ...style,
        }}
      >
        <Info size={14} />
        Get Image Sizing Info
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10, 15, 25, 0.82)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32,
          }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            aria-label="Close"
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: 999, width: 40, height: 40, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={20} />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 640, width: '100%', background: '#fff', color: 'var(--gray-800, #1f2937)',
              borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              padding: '24px 28px', fontSize: 14, lineHeight: 1.55,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Info size={20} style={{ color: 'var(--navy, #1e3a8a)' }} />
              <h2 style={{ margin: 0, fontSize: 18, color: 'var(--navy, #1e3a8a)' }}>Banner image sizing</h2>
            </div>

            <p style={{ margin: '8px 0 14px', color: 'var(--gray-600, #4b5563)', fontSize: 13 }}>
              The storefront banner renders at the full viewport width with a fixed 120&nbsp;px height.
              Viewport widths range from ~375&nbsp;px (mobile) to 2560&nbsp;px+ (ultrawide), so
              <code style={codeStyle}>object-fit: cover</code> always crops <em>something</em> — sides on narrow screens,
              top &amp; bottom on wide screens. No single image size shows the full banner uncropped everywhere; design for a
              center <strong>safe zone</strong> that survives every crop.
            </p>

            <ol style={{ margin: '10px 0 6px 20px', padding: 0 }}>
              <li style={liStyle}>
                Canvas <strong>2560 × 400</strong> (or wider).
              </li>
              <li style={liStyle}>
                Keep subject + text within the <strong>center 1400 × 120</strong> region — that zone survives on every viewport.
              </li>
              <li style={liStyle}>
                Let the edges be expendable background (gradient, water, texture) — anything that can be safely cropped on narrow screens.
              </li>
              <li style={liStyle}>
                Subject <strong>vertically centered</strong>, not anchored to the top or bottom.
              </li>
            </ol>

            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  padding: '8px 16px', border: '1px solid var(--gray-300, #d1d5db)',
                  background: 'var(--navy, #1e3a8a)', color: '#fff', borderRadius: 6,
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const codeStyle = {
  background: 'var(--gray-100, #f3f4f6)',
  padding: '1px 5px',
  borderRadius: 3,
  fontSize: 12,
  margin: '0 2px',
};

const liStyle = { marginBottom: 6 };
